
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import { UserNav } from "@/components/user-nav"
import { useView } from "@/contexts/view-context"
import { useMobile } from "@/contexts/mobile-context"
import { UserSearch } from "@/components/header/user-search"
import { GymSwitcher } from "@/components/header/gym-switcher"
import { SoundToggle } from "@/components/sound-toggle"
import { ThemeToggle } from "@/components/theme-toggle"
import { Menu } from "lucide-react"

interface HeaderProps {
  showSidebarToggle?: boolean
}

export function Header({ showSidebarToggle = true }: HeaderProps) {
  const { t } = useTranslation()
  const { viewMode } = useView()
  const { isMobile, toggleSidebar } = useMobile()

  const showSearch = viewMode === "admin" || viewMode === "reception"
  const title =
    viewMode === "user"
      ? t("userView.title")
      : viewMode === "trainer"
        ? t("trainerView.title")
        : viewMode === "reception"
          ? t("receptionView.title")
          : null

  return (
    <header className="sticky top-0 z-20 w-full border-b border-border/60 bg-card">
      <div className="flex h-14 items-center gap-2 px-3 sm:px-4 md:px-6">
        {/* Mobile sidebar toggle */}
        {showSidebarToggle && isMobile && (
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleSidebar}
            className="shrink-0"
            aria-label="Toggle sidebar"
          >
            <Menu className="h-5 w-5" />
          </Button>
        )}

        {/* Title for non-admin views */}
        {title && (
          <div className="truncate text-base font-semibold tracking-tight">{title}</div>
        )}

        {/* User search — visible from md up (admin / reception) */}
        {showSearch && (
          <div className="hidden w-full max-w-sm md:block">
            <UserSearch onUserSelect={() => {}} />
          </div>
        )}

        <div className="flex-1" />

        {/* Right cluster: gym switcher + actions */}
        <div className="flex shrink-0 items-center gap-1 sm:gap-2">
          <GymSwitcher />
          <div className="flex items-center gap-0.5 sm:gap-1">
            <ThemeToggle
              variant="ghost"
              size="icon"
              className="text-muted-foreground hover:text-foreground"
            />
            <SoundToggle />
            <UserNav />
          </div>
        </div>
      </div>
    </header>
  )
}
